import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import get from 'lodash/get';

import Card from '@material-ui/core/Card';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

import ArrowBackTwoToneIcon from '@material-ui/icons/ArrowBackTwoTone';

import PlanCard from '@/components/PlanCard';
import Snackbar from '@/components/Snackbar';

import '@/styles/form.scss';

import AccountDetailsScreen from './components/AccountDetailsScreen';

import './Checkout.styles.scss';
import { SCREEN } from './Checkout.constants';

const Checkout = ({ controller }) => (
  <div className="Checkout">
    <title>Checkout</title>
    <div className="liner">
      <div className="summary">
        <div className="header">
          <IconButton
            className="back-icon"
            onClick={controller.handleGoBackPress}
          >
            <ArrowBackTwoToneIcon />
          </IconButton>
          <Typography className="back-label">Pick Plan</Typography>
        </div>
        <div className="content">
          <div className="details-header">
            <Typography variant="h5">Subscription Details</Typography>
          </div>

          {controller.product && (
            <div className="plan-card-container">
              <PlanCard
                name={get(controller, 'product.name')}
                price={get(controller, 'product.price')}
                currency={get(controller, 'product.currency')}
                interval="month"
                exempt={Boolean(
                  get(controller, 'affiliatePlan.affiliateMemberExempt')
                )}
                discount={get(
                  controller,
                  'affiliatePlan.affiliateMemberDiscount',
                  undefined
                )}
                trial={controller.trialDuration}
              />
            </div>
          )}

          <div className="totals">
            <div className="totals-row">
              <Typography>Tax</Typography>
              <Typography>
                {!!controller.tax && controller.tax}
                {!controller.tax && '---'}
              </Typography>
            </div>

            <Divider />

            <div className="totals-row">
              <Typography>Total</Typography>
              <Typography>
                {controller.total ? controller.total : '---'}
              </Typography>
            </div>

            <Divider />

            {!!controller.trialDuration && (
              <div className="totals-row">
                <Typography>First Payment</Typography>
                <Typography>
                  {moment()
                    .add(...controller.trialDuration)
                    .format('Do MMM YYYY')}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>

      <Card className="checkout-form" elevation={5}>
        {controller.isSessionLoading && (
          <div className="progress-screen">
            <CircularProgress />
          </div>
        )}

        {!controller.isSessionLoading && (
          <>
            {controller.currentScreen === SCREEN.ACCOUNT_DETAILS && (
              <AccountDetailsScreen controller={controller} />
            )}
          </>
        )}
      </Card>
    </div>
    <Snackbar
      open={controller.errors.length > 0}
      message={controller.errors.join(' ')}
      onClose={controller.handleErrorsClose}
    />
  </div>
);

Checkout.propTypes = {
  controller: PropTypes.shape({
    errors: PropTypes.arrayOf(PropTypes.string).isRequired,
    session: PropTypes.shape({
      complete: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
      country: PropTypes.string,
      postalCode: PropTypes.string,
      priceId: PropTypes.string,
      userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      affiliatePlan: PropTypes.shape({
        affiliateMemberExempt: PropTypes.bool,
        affiliateMemberDiscount: PropTypes.number,
      }),
      price: PropTypes.shape({
        unit_amount: PropTypes.number,
        recurring: PropTypes.shape({
          interval_count: PropTypes.number,
        }),
        currency: PropTypes.string,
      }),
      product: PropTypes.shape({
        name: PropTypes.string,
        trialDuration: PropTypes.arrayOf(
          PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        ),
      }),
    }),
    currentScreen: PropTypes.oneOf(Object.values(SCREEN)).isRequired,
    product: PropTypes.shape({
      name: PropTypes.string,
      price: PropTypes.number,
      interval: PropTypes.number,
      currency: PropTypes.string,
    }),
    tax: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
    ]),
    total: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
    ]),
    trialDuration: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    ),
    currency: PropTypes.string,
    affiliatePlan: PropTypes.shape({
      affiliateMemberExempt: PropTypes.bool,
      affiliateMemberDiscount: PropTypes.number,
    }),
    isCheckingAccountDetails: PropTypes.bool.isRequired,
    isSessionLoading: PropTypes.bool.isRequired,
    isTaxInfoLoading: PropTypes.bool.isRequired,
    getCountries: PropTypes.func.isRequired,
    getCountryLabel: PropTypes.func.isRequired,
    getCountrySelected: PropTypes.func.isRequired,
    handleAccountDetailsChange: PropTypes.func.isRequired,
    handleAccountDetailsSubmit: PropTypes.func.isRequired,
    handleErrorsClose: PropTypes.func.isRequired,
    handleGoBackPress: PropTypes.func.isRequired,
  }).isRequired,
};

export default Checkout;
